import { useApolloClient } from '@apollo/client'
import { Field, Form, Formik } from 'formik'
import { useRouter } from 'next/router'
import PropTypes from 'prop-types'
import React, { useRef } from 'react'
import styled from 'styled-components'
import { space } from 'styled-system'
import * as Yup from 'yup'
import { z } from 'zod'
import { Button, BUTTON_VARIANT } from 'components/Button'
import Icons from 'components/Icon'
import { Input } from 'components/Input'
import { InvisibleRecaptcha } from 'components/InvisibleRecaptcha'
import { Flex } from 'components/Layout'
import { LinkWithText } from 'components/Link'
import PasswordInput from 'components/PasswordInput'
import { Text } from 'components/Typography'
import { PASSWORD_VALIDATION } from 'constants/common'
import { ROUTE } from 'constants/routes'
import { useGoogleAnalytics } from 'providers/googleAnalytics'
import { useUserAuth } from 'providers/userAuth'
import { PROVIDER_METHODS } from 'providers/userAuthUtil'
import { COLOR, FONT_SIZE, FONT_WEIGHT, SPACE } from 'Theme'
import {
  isExpertApplicationEmailValid,
  safeStringSchema,
} from 'utils/validation'

const SocialForm = styled('form')`
  width: 100%;
  margin-top: ${SPACE.PX_30};
  ${space}
`

const ClientSignUp = () => {
  const userAuth = useUserAuth()
  const apolloClient = useApolloClient()
  const router = useRouter()
  const invisibleRecaptchaRef = useRef(null)
  const { executeDataToDataLayer } = useGoogleAnalytics()

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .label('Password')
      .min(8, 'Minimum 8 characters')
      .matches(...Object.values(PASSWORD_VALIDATION))
      .required(),
    firstName: safeStringSchema.label('First Name').required(),
    lastName: safeStringSchema.label('Last Name').required(),
    email: Yup.string()
      .email('Invalid email')
      .trim()
      .label('Email')
      .test(
        'valid-email',
        '',
        // eslint-disable-next-line func-names
        async function (value) {
          const { path, createError } = this

          const validEmail = z.string().email().safeParse(value).success
          if (!validEmail) {
            return createError({
              path,
              message: 'Invalid email format',
            })
          }

          const exists = await isExpertApplicationEmailValid({
            email: value,
            apolloClient,
            omitRejected: true,
          })

          if (!exists) {
            return createError({
              path,
              message: 'This user already has an account',
            })
          }

          return true
        }
      )
      .required(),
  })

  const { query } = router
  return (
    <>
      <SocialForm mt={0}>
        <Button
          onClick={() => userAuth.thirdPartySignIn(PROVIDER_METHODS.GOOGLE)}
          variant={BUTTON_VARIANT.OUTLINE}
          icon={Icons.Google}
        >
          Sign up with Google
        </Button>
        {/* TODO: uncomment after we'll handle FB social login
        <Button
          onClick={() => userAuth.thirdPartySignIn(PROVIDER_METHODS.FACEBOOK)}
          variant={BUTTON_VARIANT.OUTLINE}
          mt={SPACE.PX_15}
          icon={Icons.Facebook}
        >
          Sign up with Facebook
        </Button>
        */}
        <Flex width="100%" justifyContent="center" my={SPACE.PX_10}>
          <Text color={COLOR.GRAYSCALE_2} fontSize={FONT_SIZE.PX_12}>
            or sign up using email
          </Text>
        </Flex>
      </SocialForm>
      <Formik
        initialValues={{
          firstName: '',
          lastName: '',
          password: '',
          email: query?.email ?? '',
        }}
        validationSchema={validationSchema}
        validateOnChange={false}
        onSubmit={async ({ email, ...restValues }, { resetForm }) => {
          const allowSignUp = await invisibleRecaptchaRef.current?.execute()
          if (allowSignUp) {
            const shouldResetForm = await userAuth.signUp({
              executeDataToDataLayer,
              email: email.trim(),
              ...restValues,
            })
            if (shouldResetForm) {
              resetForm()
            }
          }
        }}
      >
        {({ handleSubmit }) => (
          <Form onSubmit={handleSubmit}>
            <Field name="firstName">
              {({ field, meta }) => (
                <Input
                  label="First name"
                  isDisabled={userAuth.isLoading}
                  isError={meta.touched && meta.error !== undefined}
                  hint={meta.touched ? meta.error : ''}
                  hasHint
                  {...field}
                />
              )}
            </Field>
            <Field name="lastName">
              {({ field, meta }) => (
                <Input
                  label="Last name"
                  isDisabled={userAuth.isLoading}
                  isError={meta.touched && meta.error !== undefined}
                  hint={meta.touched ? meta.error : ''}
                  hasHint
                  {...field}
                />
              )}
            </Field>
            <Field name="email">
              {({ field, meta }) => (
                <Input
                  label="Email address"
                  isDisabled={userAuth.isLoading}
                  isError={meta.touched && meta.error !== undefined}
                  hint={meta.touched ? meta.error : ''}
                  hasHint
                  {...field}
                />
              )}
            </Field>
            <Field name="password">
              {({ field, meta }) => (
                <PasswordInput
                  hasVisibilityButton
                  isDisabled={userAuth.isLoading}
                  isError={meta.touched && meta.error !== undefined}
                  hint={meta.touched ? meta.error : ''}
                  hasHint
                  {...field}
                />
              )}
            </Field>
            <Flex width="100%" mb={SPACE.PX_15}>
              <Text
                px={SPACE.PX_30}
                fontSize={FONT_SIZE.PX_12}
                textAlign="center"
                color={COLOR.PRIMARY_1}
              >
                By selecting agree and continue below, I agree to
                <LinkWithText
                  fontWeight={FONT_WEIGHT.BOLD}
                  fontSize={FONT_SIZE.PX_12}
                  href={ROUTE.TERMS_OF_USE()}
                >
                  {` The Expert's terms of use`}
                </LinkWithText>
              </Text>
            </Flex>
            <InvisibleRecaptcha ref={invisibleRecaptchaRef} />
            <Button isLoading={userAuth.isLoading} type="submit">
              Agree and Continue
            </Button>
          </Form>
        )}
      </Formik>
    </>
  )
}

ClientSignUp.propTypes = {
  onSignUpCompleted: PropTypes.func,
}

export default ClientSignUp
